import { mdiClose, mdiSubdirectoryArrowRight } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';
import { CreatableInput, ISelectOption } from '../../base';
import {
    AutomationRule,
    DateOfServiceMatchValue,
    RuleMatch,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    conditionMetaForScope,
    formatFilterGroupTitle,
    getConditionKindsForScope,
    getScopeDescription,
    getScopeIcon,
    getScopeSelectLabel,
    isDateOfServiceMatchValue,
} from '../../../services/worker-configuration/automation/worker-automation-scope.registry';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { ConfigurationRuleConditionEditor } from './configuration-rule-condition-editor';
import { ConfigurationRuleDosEditor } from './configuration-rule-dos-editor';
import { ConfigurationRuleEntityEditor } from './configuration-rule-entity-editor';
import { ConfigurationRulePayerEditor } from './configuration-rule-payer-editor';
import { getSingleSelectOption } from '../configuration-base-components';
import * as S from './configuration-rule.styles';

interface IConfigurationRuleMatchBlockProps {
    availableScopes: string[];
    disabled?: boolean;
    match: RuleMatch;
    matchIndex: number;
    removable: boolean;
    rule: AutomationRule;
    store: WorkerStore;
}

function isEntityScope(scope: string): scope is 'BILLING_PROVIDER' | 'RENDERING_PROVIDER' | 'FACILITY' {
    return scope === 'BILLING_PROVIDER' || scope === 'RENDERING_PROVIDER' || scope === 'FACILITY';
}

export const ConfigurationRuleMatchBlock: React.FC<IConfigurationRuleMatchBlockProps> = observer(
    ({ availableScopes, disabled, match, matchIndex, removable, rule, store }) => {
        const kinds = getConditionKindsForScope(match.scope);
        const isPayerScope = match.scope === 'PAYER';
        const isDosScope = match.scope === 'DATE_OF_SERVICE';
        const isProcedureScope = match.scope === 'PROCEDURE_CODE';
        const isEntity = isEntityScope(match.scope);
        const hideConditionIntro = isPayerScope || isEntity || isDosScope || isProcedureScope;
        const scopeOptions = useMemo<ISelectOption<string>[]>(
            () =>
                availableScopes.map(scope => ({
                    label: getScopeSelectLabel(scope),
                    value: scope,
                })),
            [availableScopes],
        );
        const selectedScope =
            scopeOptions.find(option => option.value === match.scope) ?? scopeOptions[0];

        const dosValue: DateOfServiceMatchValue = isDateOfServiceMatchValue(match.value)
            ? match.value
            : { fromDos: null, throughDos: null };

        return (
            <S.MatchBlock>
                <S.MatchBlockHeader>
                    <S.MatchBlockScopeIcon aria-hidden={true}>
                        <Icon path={getScopeIcon(match.scope)} size={0.85} />
                    </S.MatchBlockScopeIcon>

                    <S.MatchBlockScopeColumn>
                        <S.MatchBlockEyebrow>
                            Filter group · {formatFilterGroupTitle(matchIndex)}
                        </S.MatchBlockEyebrow>
                        <S.MatchBlockScopeSelect
                            options={scopeOptions}
                            value={selectedScope}
                            required
                            disabled={disabled}
                            onChange={selected => {
                                const option = getSingleSelectOption(selected);
                                store.setMatchBlockScope(
                                    rule.id,
                                    matchIndex,
                                    String(option?.value ?? match.scope),
                                );
                            }}
                        />
                    </S.MatchBlockScopeColumn>

                    <S.MatchBlockHeaderDescription>
                        {getScopeDescription(match.scope)}
                    </S.MatchBlockHeaderDescription>

                    {removable && (
                        <S.MatchBlockRemove
                            type="button"
                            disabled={disabled}
                            aria-label="Remove match condition"
                            onClick={() => store.removeMatchBlock(rule.id, matchIndex)}
                        >
                            <Icon path={mdiClose} size={0.7} aria-hidden={true} />
                        </S.MatchBlockRemove>
                    )}
                </S.MatchBlockHeader>

                <S.MatchBlockBody>
                    {!hideConditionIntro && (
                        <S.MatchConditionIntro>
                            <Icon path={mdiSubdirectoryArrowRight} size={0.65} aria-hidden={true} />
                            Conditions in this filter group
                        </S.MatchConditionIntro>
                    )}

                    <S.MatchConditionStack>
                        {isPayerScope ? (
                            <ConfigurationRulePayerEditor
                                ruleId={rule.id}
                                matchIndex={matchIndex}
                                payerIds={store.getConditionValuesForMatch(rule, matchIndex, 'payerId')}
                                store={store}
                                disabled={disabled}
                            />
                        ) : isDosScope ? (
                            <ConfigurationRuleDosEditor
                                ruleId={rule.id}
                                matchIndex={matchIndex}
                                value={dosValue}
                                store={store}
                                disabled={disabled}
                            />
                        ) : match.scope === 'BILLING_PROVIDER' ? (
                            <ConfigurationRuleEntityEditor
                                ruleId={rule.id}
                                matchIndex={matchIndex}
                                kind="billingProviderId"
                                entityIds={store.getConditionValuesForMatch(
                                    rule,
                                    matchIndex,
                                    'billingProviderId',
                                )}
                                store={store}
                                disabled={disabled}
                            />
                        ) : match.scope === 'RENDERING_PROVIDER' ? (
                            <ConfigurationRuleEntityEditor
                                ruleId={rule.id}
                                matchIndex={matchIndex}
                                kind="renderingProviderId"
                                entityIds={store.getConditionValuesForMatch(
                                    rule,
                                    matchIndex,
                                    'renderingProviderId',
                                )}
                                store={store}
                                disabled={disabled}
                            />
                        ) : match.scope === 'FACILITY' ? (
                            <ConfigurationRuleEntityEditor
                                ruleId={rule.id}
                                matchIndex={matchIndex}
                                kind="facilityId"
                                entityIds={store.getConditionValuesForMatch(
                                    rule,
                                    matchIndex,
                                    'facilityId',
                                )}
                                store={store}
                                disabled={disabled}
                            />
                        ) : isProcedureScope ? (
                            <S.PayerEditor>
                                <S.PayerSectionHint>
                                    <Icon
                                        path={mdiSubdirectoryArrowRight}
                                        size={0.75}
                                        color="#8e9293"
                                        aria-hidden={true}
                                    />
                                    Matches any of these procedure codes (OR)
                                </S.PayerSectionHint>
                                <CreatableInput
                                    disabled={disabled}
                                    placeholder="e.g. 99213 + Enter"
                                    value={store.getConditionValuesForMatch(
                                        rule,
                                        matchIndex,
                                        'procedureCode',
                                    )}
                                    onChange={values => {
                                        store.setConditionValues(
                                            rule.id,
                                            matchIndex,
                                            'procedureCode',
                                            values,
                                        );
                                    }}
                                />
                            </S.PayerEditor>
                        ) : (
                            kinds.map(kind => {
                                const meta = conditionMetaForScope(match.scope, kind);
                                return (
                                    <ConfigurationRuleConditionEditor
                                        key={kind}
                                        ruleId={rule.id}
                                        matchIndex={matchIndex}
                                        kind={kind}
                                        label={meta.label}
                                        hint={meta.hint}
                                        placeholder={
                                            kind === 'contains'
                                                ? 'Paste rejection message + Enter'
                                                : kind === 'carcGroup'
                                                  ? 'Search CARC groups…'
                                                  : kind === 'carcCode'
                                                    ? 'e.g. 261 + Enter'
                                                    : 'e.g. N687 + Enter'
                                        }
                                        values={store.getConditionValuesForMatch(
                                            rule,
                                            matchIndex,
                                            kind,
                                        )}
                                        store={store}
                                        disabled={disabled}
                                    />
                                );
                            })
                        )}
                    </S.MatchConditionStack>
                </S.MatchBlockBody>
            </S.MatchBlock>
        );
    },
);
